import { Routes } from '@angular/router';
import { AppLayoutComponent } from '@shared/layout/app-layout/app-layout.component';
import { authGuard } from '@shared/auth/guards/auth.guard';
import { centralClientGuard } from '@shared/auth/guards/central-client.guard';
import { redirectAuthenticatedGuard } from '@shared/auth/guards/redirect-authenticated.guard';
import { adminGuard } from '@shared/auth/guards/admin.guard';
import { permissionGuard, requireActionGuard } from '@shared/auth/guards/permission.guard';
import { transferenciaTokenGuard } from '@partnercore/feature-settings/configuracion/transferencia-confirmar/transferencia-token.guard';
import { transferenciaConfirmarResolver } from '@partnercore/feature-settings/configuracion/transferencia-confirmar/transferencia-confirmar.resolver';
import { localOnlyGuard } from './guards/local-only.guard';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'auth/login',
    pathMatch: 'full',
  },
  {
    path: '',
    component: AppLayoutComponent,
    canActivate: [authGuard, centralClientGuard],
    children: [
      {
        path: 'inicio',
        loadComponent: () => import('@partnercore/feature-home/inicio/inicio.component').then(m => m.InicioComponent),
        title: 'titles.inicio',
      },
      {
        path: 'notifications',
        loadComponent: () => import('@partnercore/feature-modules/modulos/control-general/notificaciones/notifications/notifications.component').then(m => m.NotificationsComponent),
        title: 'titles.notificaciones',
      },
      {
        path: 'configuracion',
        loadComponent: () => import('@partnercore/feature-settings/configuracion/configuracion.component').then(m => m.ConfiguracionComponent),
        title: 'titles.configuracion',
      },
      {
        path: 'soporte',
        loadComponent: () => import('@partnercore/feature-settings/soporte/soporte.component').then(m => m.SoporteComponent),
        title: 'titles.soporte',
      },
      // EJEMPLO: módulo de negocio propio del proyecto (ver docs/modulos-de-ejemplo en partnercore_backend).
      // El path coincide EXACTO con la url registrada en EjemploModuleCatalog (backend) para que el
      // sidebar y los permisos por módulo encajen. Bórralo al crear tus módulos reales.
      {
        path: 'ejemplos/modulo-a',
        loadComponent: () => import('./ejemplo-modulo-a/ejemplo-modulo-a.component').then(m => m.EjemploModuloAComponent),
        title: 'Módulo A',
      },
      {
        path: 'configuracion/transferir',
        loadComponent: () => import('@partnercore/feature-settings/configuracion/transferencia-confirmar/transferencia-confirmar.component').then(m => m.TransferenciaConfirmarComponent),
        title: 'titles.confirmTransfer',
        canActivate: [transferenciaTokenGuard],
        resolve: { transferenciaInfo: transferenciaConfirmarResolver },
      },
      // Dashboard Sistema
      {
        path: 'dashboard-system',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'dashboard-general',
            pathMatch: 'full',
          },
          {
            path: 'dashboard-general',
            loadComponent: () => import('@partnercore/feature-dashboard/dashboard/general/dashboard-general.component').then(m => m.DashboardGeneralComponent),
            canActivate: [permissionGuard],
            title: 'titles.dashboardGeneral',
          },
        ],
      },
      // Módulos Admin Sistema
      {
        path: 'admin-sistema',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'usuarios',
            pathMatch: 'full',
          },
          {
            path: 'usuarios',
            loadComponent: () => import('@partnercore/feature-admin/users/users.component').then(m => m.UsersComponent),
            canActivate: [permissionGuard],
            title: 'titles.usuariosSistema',
          },
          {
            path: 'permisos',
            loadComponent: () => import('@partnercore/feature-admin/permisos').then(m => m.PermisosComponent),
            canActivate: [permissionGuard],
            title: 'titles.permisosSistema',
          },
          {
            path: 'permisos/editar/rol/:id',
            loadComponent: () => import('@partnercore/feature-admin/permisos-editar').then(m => m.PermisosEditarComponent),
            canActivate: [permissionGuard, requireActionGuard('edit')],
            title: 'titles.permisosSistema',
          },
          {
            path: 'roles',
            loadComponent: () => import('@partnercore/feature-admin/roles/roles.component').then(m => m.RolesComponent),
            canActivate: [permissionGuard],
            title: 'titles.rolesSistema',
          },
          {
            path: 'perfil',
            loadComponent: () => import('@partnercore/feature-profile/perfil/perfil.component').then(m => m.PerfilComponent),
            canActivate: [permissionGuard],
            title: 'titles.perfilSistema',
          },
          {
            path: 'empresas',
            loadComponent: () => import('@partnercore/feature-admin/empresas/empresas.component').then(m => m.EmpresasComponent),
            canActivate: [permissionGuard],
            title: 'titles.empresasSistema',
          },
          {
            path: 'empresas/:id',
            loadComponent: () => import('@partnercore/feature-admin/empresas/components/empresa-detail/empresa-detail.component').then(m => m.EmpresaDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.empresaDetalle',
          },
          {
            path: 'clientes',
            loadComponent: () => import('@partnercore/feature-admin/clientes/clientes.component').then(m => m.ClientesComponent),
            canActivate: [permissionGuard],
            title: 'titles.clientesSistema',
          },
          {
            path: 'planes',
            loadComponent: () => import('@partnercore/feature-admin/planes/planes.component').then(m => m.PlanesComponent),
            canActivate: [permissionGuard],
            title: 'titles.planesSistema',
          },
          {
            path: 'planes/:id',
            loadComponent: () => import('@partnercore/feature-admin/planes/components/plan-detail/plan-detail.component').then(m => m.PlanDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.planesSistema',
          },
          {
            path: 'productos',
            loadComponent: () => import('@partnercore/feature-admin/productos/productos.component').then(m => m.ProductosComponent),
            canActivate: [permissionGuard],
            title: 'titles.productosSistema',
          },
        ],
      },
      // Seguridad Sistema (SYS) — ruta top-level para que coincida con la URL sembrada en DB: /seguridad-sistema/*
      {
        path: 'seguridad-sistema',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'sesiones',
            pathMatch: 'full',
          },
          {
            path: 'sesiones',
            loadComponent: () => import('@partnercore/feature-sessions/sesiones/sesiones.component').then(m => m.SesionesComponent),
            canActivate: [permissionGuard],
            data: { isSys: true },
            title: 'titles.sesionesSistema',
          },
          {
            path: 'auditorias',
            loadComponent: () => import('@partnercore/feature-audit/auditoria/auditoria.component').then(m => m.AuditoriaComponent),
            canActivate: [permissionGuard],
            data: { isSys: true },
            title: 'titles.auditSistema',
          },
        ],
      },
      // Dashboard Control General
      {
        path: 'dashboard',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'general',
            pathMatch: 'full',
          },
          {
            path: 'general',
            loadComponent: () => import('@partnercore/feature-dashboard/dashboard/general/dashboard-general.component').then(m => m.DashboardGeneralComponent),
            canActivate: [permissionGuard],
            title: 'titles.dashboardGeneral',
          },
        ],
      },
      // Módulos Administrativos
      {
        path: 'admin',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'usuarios',
            pathMatch: 'full',
          },
          {
            path: 'usuarios',
            loadComponent: () => import('@partnercore/feature-admin/users/users.component').then(m => m.UsersComponent),
            canActivate: [permissionGuard],
            title: 'titles.usuarios',
          },
          {
            path: 'permisos',
            loadComponent: () => import('@partnercore/feature-admin/permisos').then(m => m.PermisosComponent),
            canActivate: [permissionGuard],
            title: 'titles.permisos',
          },
          {
            path: 'permisos/editar/rol/:id',
            loadComponent: () => import('@partnercore/feature-admin/permisos-editar').then(m => m.PermisosEditarComponent),
            canActivate: [permissionGuard, requireActionGuard('edit')],
            title: 'titles.permisos',
          },
          {
            path: 'roles',
            loadComponent: () => import('@partnercore/feature-admin/roles/roles.component').then(m => m.RolesComponent),
            canActivate: [permissionGuard],
            title: 'titles.rolesAdmin',
          },
          {
            path: 'perfil',
            loadComponent: () => import('@partnercore/feature-profile/perfil/perfil.component').then(m => m.PerfilComponent),
            canActivate: [permissionGuard],
            title: 'titles.perfil',
          },
          {
            path: 'empresas',
            loadComponent: () => import('@partnercore/feature-admin/empresas/empresas.component').then(m => m.EmpresasComponent),
            canActivate: [permissionGuard],
            title: 'titles.empresas',
          },
          {
            path: 'empresas/:id',
            loadComponent: () => import('@partnercore/feature-admin/empresas/components/empresa-detail/empresa-detail.component').then(m => m.EmpresaDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.empresaDetalle',
          },
        ],
      },
      // Estructura
      {
        path: 'estructura-system',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'modulos-sistema',
            pathMatch: 'full',
          },
          {
            path: 'modulos-sistema',
            loadComponent: () => import('@partnercore/feature-modules/modulos/module.component').then(m => m.ModuleComponent),
            canActivate: [permissionGuard],
            title: 'titles.modulosSistema',
          },
        ],
      },
      // Maestros Sistema
      {
        path: 'maestros-sistema',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'monedas',
            pathMatch: 'full',
          },
          {
            path: 'monedas',
            loadComponent: () => import('@partnercore/feature-currencies/currencies/currencies.component').then(m => m.CurrenciesComponent),
            canActivate: [permissionGuard],
            title: 'titles.monedasSistema',
          },
        ],
      },
      // Maestros
      {
        path: 'maestros',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'monedas',
            pathMatch: 'full',
          },
          {
            path: 'monedas',
            loadComponent: () => import('@partnercore/feature-currencies/currencies/currencies.component').then(m => m.CurrenciesComponent),
            canActivate: [permissionGuard],
            title: 'titles.monedas',
          },
        ],
      },
      // Seguridad Control General — ruta top-level para que coincida con la URL sembrada en DB: /seguridad/*
      {
        path: 'seguridad',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'sesiones',
            pathMatch: 'full',
          },
          {
            path: 'sesiones',
            loadComponent: () => import('@partnercore/feature-sessions/sesiones/sesiones.component').then(m => m.SesionesComponent),
            canActivate: [permissionGuard],
            data: { isSys: false },
            title: 'titles.sesiones',
          },
          {
            path: 'auditorias',
            loadComponent: () => import('@partnercore/feature-audit/auditoria/auditoria.component').then(m => m.AuditoriaComponent),
            canActivate: [permissionGuard],
            data: { isSys: false },
            title: 'titles.audit',
          },
        ],
      },
    ],
  },
  // Catálogo vivo de los componentes de la dependencia (@shared/components/*). Vive DENTRO de
  // la dependencia (shared/ui/src/docs/...) — acá solo se registra la ruta para exponerla.
  // Página aparte, fuera del AppLayoutComponent (sin sidebar/header de la app): necesita el
  // ancho completo y su propio scroll, y anidar su sidebar dentro del layout general chocaba
  // con el sidebar colapsable de la app. Sin login (son datos de ejemplo, nada sensible) pero
  // SOLO accesible corriendo local — no debe existir en ningún ambiente desplegado.
  {
    path: 'partnercore-components',
    loadComponent: () => import('@shared/components/docs/partnercore-components-doc/partnercore-components-doc.component').then(m => m.PartnercoreComponentsDocComponent),
    canActivate: [localOnlyGuard],
    title: 'PartnerCore Components',
  },
  // Auth
  {
    path: 'auth',
    loadComponent: () => import('@partnercore/feature-auth-pages/auth/auth.component').then(m => m.AuthComponent),
    canActivate: [redirectAuthenticatedGuard],
    children: [
      {
        path: 'login',
        loadComponent: () => import('@partnercore/feature-auth-pages/auth-pages/login/login.component-page').then(m => m.LoginPageComponent),
      },
      {
        path: 'sin-acceso',
        loadComponent: () => import('@partnercore/feature-auth-pages/auth-pages/sin-acceso/sin-acceso.component-page').then(m => m.SinAccesoPageComponent),
      },
      {
        path: 'central-no-disponible',
        loadComponent: () => import('@partnercore/feature-auth-pages/auth-pages/central-no-disponible/central-no-disponible.component-page').then(m => m.CentralNoDisponiblePageComponent),
      },
      {
        path: 'forgot-password',
        loadComponent: () => import('@partnercore/feature-auth-pages/auth-pages/forgot-password/forgot-password-page.component').then(m => m.ForgotPasswordPageComponent),
      },
      {
        path: 'reset-password',
        loadComponent: () => import('@partnercore/feature-auth-pages/auth-pages/reset-password/reset-password-page.component').then(m => m.ResetPasswordPageComponent),
      },
    ],
  },
  // Error
  {
    path: '404',
    loadComponent: () => import('@partnercore/feature-error-pages/other-page/not-found/not-found.component').then(m => m.NotFoundComponent),
    title: 'titles.notFound',
  },
  {
    path: '**',
    loadComponent: () => import('@partnercore/feature-error-pages/other-page/not-found/not-found.component').then(m => m.NotFoundComponent),
    title: 'titles.notFound',
  },
];
